import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../app/theme';
import { ScheduleType } from '../../app/types';
import IcHelpCircle from '../../assets/images/svg/ic-help-circle.svg';
import { setNodeData } from '../../features/mainSlice';
import Dropdown from '../shared/Dropdown';
import MonthSchedule from '../shared/MonthSchedule';
import TimePicker from '../shared/TimePicker';
import Tooltip from '../shared/Tooltip';
import WeekSchedule from '../shared/WeekSchedule';
import Mask from './SettingBarMask';

const useStyles = makeStyles(
  {
    top: {
      fontSize: '17px',
      fontWeight: 'normal',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      background: color.$9,
      textAlign: 'center',
      padding: '18px 0 21px',
    },
    bottom: {
      padding: '24px 16px',
      overflowY: 'auto',
      height: 'calc(100vh - 170px)',
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 15,
      lineHeight: '15px',
      color: color.$3,
      marginBottom: 12,
      display: 'flex',
      alignItems: 'center',
      '& > img': {
        marginLeft: 4,
      },
    },
    time: {
      width: 100,
    },
    marginBottom12: {
      marginBottom: 12,
    },
    hasLastDay: {
      fontSize: '15px',
      lineHeight: '21px',
      color: color.$13,
    },
  },
  {
    name: 'ScheduleSettingBar',
  },
);

const ScheduleSettingBar = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selectedNode } = useSelector(state => state.ma);
  const { type, time, week, month, hasLastDay } = selectedNode?.data;

  const typeItem = Object.values(ScheduleType).map(value => ({
    text: t(`scheduleType.${value}`),
    value,
  }));

  const settingComponent = useMemo(() => {
    switch (type) {
      case ScheduleType.DAY:
        return null;
      case ScheduleType.WEEK:
        return (
          <WeekSchedule
            className={classes.marginBottom12}
            data={week}
            onChange={value => {
              dispatch(setNodeData({ week: value }));
            }}
          />
        );
      case ScheduleType.MONTH:
        return (
          <>
            <MonthSchedule
              className={classes.marginBottom12}
              data={month}
              onChange={value => {
                dispatch(setNodeData({ month: value }));
              }}
            />
            <FormControlLabel
              className={classes.marginBottom12}
              control={
                <Checkbox
                  checked={hasLastDay}
                  onChange={e => {
                    dispatch(setNodeData({ hasLastDay: e.target.checked }));
                  }}
                  name="autoTrackProduct"
                  color="primary"
                />
              }
              label={
                <div className={classes.hasLastDay}>{t('hasLastDay')}</div>
              }
            />
          </>
        );
      default:
        return null;
    }
  }, [selectedNode?.data]);

  const onChangeType = value => {
    switch (value) {
      case ScheduleType.DAY:
        dispatch(setNodeData({ type: value, time }));
        break;
      case ScheduleType.WEEK:
        dispatch(setNodeData({ type: value, time, week: [0] }));
        break;
      case ScheduleType.MONTH:
        dispatch(
          setNodeData({ type: value, time, month: [1, 15], hasLastDay: false }),
        );
        break;
      default:
        break;
    }
  };

  const onChangetime = value => {
    dispatch(setNodeData({ time: moment(value).format('HH:mm') }));
  };

  return (
    <>
      <div className={classes.top}>{t('schedule')}</div>
      <div id="tag-setting-bar-bottom" className={classes.bottom}>
        <div className={classes.title}>
          {t('scheduleSetting')}
          <Tooltip
            placement="top"
            light
            title={t('scheduleSettingTooltip')}
            detailClasses={{
              popper: classes.tooltipPopper,
            }}
          >
            <img src={IcHelpCircle} alt="wait title" />
          </Tooltip>
        </div>
        <Dropdown
          className={classes.marginBottom12}
          menuItems={typeItem}
          value={type}
          onChange={onChangeType}
        />
        {settingComponent}
        <TimePicker
          className={classes.time}
          selectedTime={`${moment().format('YYYY-MM-DD')} ${time}`}
          onChange={onChangetime}
          placeholder={t('time-placeholder')}
        />
      </div>
      <Mask mainId="tag-setting-bar-bottom" />
    </>
  );
};

export default ScheduleSettingBar;
